import { t } from 'i18next';
import { ServiceAdminRole } from '~graphql/__generated__/types';

export type ServiceAdminRoleOption = {
  role: ServiceAdminRole;
  label: string;
};

export type ServiceAdminRoleCategory = {
  key: string;
  label: string;
  options: ServiceAdminRoleOption[];
};

// Mirrors SERVICE_ADMIN_ROLE_GROUPS in
// api/service/src/modules/organizations/serviceAdminRoles.ts — keep in sync if the backend ever
// adds/removes a Service Admin role or category. Options are ordered least to most privileged;
// "None" is handled separately by the consuming radio group, not listed here.
export function getServiceAdminRoleCategories(): ServiceAdminRoleCategory[] {
  return [
    {
      key: 'superAdmin',
      label: t('superAdmin'),
      options: [
        { role: ServiceAdminRole.SuperAdminUserManagement, label: t('userManagement') },
        { role: ServiceAdminRole.SuperAdmin, label: t('fullManagement') },
      ],
    },
    {
      key: 'zte',
      label: t('zte'),
      options: [
        { role: ServiceAdminRole.ServiceAdminZteView, label: t('view') },
        { role: ServiceAdminRole.ServiceAdminZteFull, label: t('fullManagement') },
      ],
    },
    {
      key: 'deviceManagement',
      label: t('deviceManagement'),
      options: [
        { role: ServiceAdminRole.ServiceAdminDeviceView, label: t('view') },
        { role: ServiceAdminRole.ServiceAdminDeviceService, label: t('serviceManagement') },
        { role: ServiceAdminRole.ServiceAdminDeviceFull, label: t('fullManagement') },
      ],
    },
    {
      key: 'firmwareManagement',
      label: t('firmwareManagement'),
      options: [
        { role: ServiceAdminRole.ServiceAdminFirmwareView, label: t('view') },
        { role: ServiceAdminRole.ServiceAdminFirmwareFull, label: t('fullManagement') },
      ],
    },
    {
      key: 'organisations',
      label: t('organisations'),
      options: [
        { role: ServiceAdminRole.ServiceAdminOrgManagementView, label: t('view') },
        { role: ServiceAdminRole.ServiceAdminOrgManagementFull, label: t('fullManagement') },
      ],
    },
  ];
}

export function getServiceAdminRoleLabel(role: ServiceAdminRole): string {
  for (const category of getServiceAdminRoleCategories()) {
    const option = category.options.find((option) => option.role === role);
    if (option) {
      return `${category.label} – ${option.label}`;
    }
  }
  return role;
}

// Just the category name (e.g. "ZTE"), without the specific privilege level (e.g. "Full
// management") — used by the Access column, which shows which areas a user has some access to
// without listing the exact level for each.
export function getServiceAdminRoleCategoryLabel(role: ServiceAdminRole): string {
  const category = getServiceAdminRoleCategories().find((category) =>
    category.options.some((option) => option.role === role),
  );
  return category?.label ?? role;
}
